import {Box, HStack, Show, Text, ThemeColorTextPath} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {MouseEvent, KeyboardEvent, forwardRef} from 'react';

import {always, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';

export interface ChipProps extends FormControlProps<boolean>, TestIdProps {
  label: string;
  suffix?: string;
  size?: 'small' | 'default';
  variant?: 'default' | 'ghost';
  isFocused?: boolean;
  isError?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  tabIndex?: number;
  ariaLabel?: string;
}

export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
  const theme = useTheme();

  const handleOnClick = (event: MouseEvent<HTMLElement>) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    props.onChange?.(props.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (props.isDisabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onChange?.(props.value);
    }

    props.onKeyDown?.(event);
  };

  const isSmall = props.size === 'small';
  const isSelected = !!props.value;

  const textColor = match<[boolean, ChipProps['variant']], ThemeColorTextPath>([
    isSelected,
    props.variant,
  ])
    .with([true, 'ghost'], always('link'))
    .with([false, 'ghost'], always('primary'))
    .with([true, Pattern.any], always('white'))
    .otherwise(always('primary'));

  const backgroundColor = match([isSelected, props.variant])
    .with([true, 'ghost'], always(theme.colors.palettes.blue[20][100]))
    .with([false, 'ghost'], always(theme.colors.general.transparent))
    .with([true, Pattern.any], always(theme.colors.general.accent))
    .otherwise(always(theme.colors.palettes.neutral['30']['100']));

  const hoverBackgroundColor = match([isSelected, props.variant])
    .with([true, 'ghost'], always(theme.colors.palettes.blue[20][100]))
    .with([true, Pattern.any], always(theme.colors.general.accent))
    .otherwise(always(theme.colors.palettes.neutral['40']['100']));

  return (
    <div
      ref={ref}
      data-testid={suffixTestId('chip', props)}
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
      tabIndex={props.tabIndex ?? -1}
      aria-checked={props.value ? true : undefined}
      aria-disabled={props.isDisabled}
      aria-label={props.ariaLabel ?? props.label}
      role="checkbox"
      css={css`
        display: inline-block;
        border-radius: ${({theme}) => theme.radii.large};
        pointer-events: ${props.isDisabled ? 'none' : 'auto'};
        opacity: ${props.isDisabled ? 0.4 : 1};
        cursor: pointer;
        transition: background-color 0.25s ease;
        background-color: ${backgroundColor};

        &:hover {
          background-color: ${hoverBackgroundColor};
        }

        ${props.isFocused &&
        css`
          outline: ${theme.outline.default} ${theme.colors.general.accent};
          outline-offset: ${theme.outline.offset};
          border-radius: ${theme.radii.large};
        `}

        ${props.isError &&
        css`
          border: 1px solid ${theme.colors.severity.danger};
        `}
      `}
    >
      <Box paddingHorizontal={isSmall ? 2 : 3}>
        <HStack align="center" spacing={1} height={isSmall ? 6 : 8}>
          <Text
            color={textColor}
            size={isSmall ? 'xSmall' : 'small'}
            noWrap
            data-testid={suffixTestId('chipContent', props)}
          >
            {props.label}
          </Text>
          <Show when={isNotNil(props.suffix)}>
            <Text color={textColor} size="xSmall" noWrap>
              {props.suffix}
            </Text>
          </Show>
        </HStack>
      </Box>
    </div>
  );
});
